import { toast, toasts } from "$service/toast";
import { get, writable } from 'svelte/store';
import {
    current_user,
    global_state,
    requests_component_lookup
} from "./store";
import { perfMonitor } from './performance-monitor';
import { apiFetch, endSessionIfExpired, onSessionExpired } from "./api";

let eventSource = null;

// Drop the stream when the session goes, rather than leaving it retrying
// against an endpoint that will keep refusing it.
onSessionExpired(() => disconnectSSE());
let SSE_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:8083';
// console.log("Using SSE_BASE_URL:", SSE_BASE_URL);

let reconnectAttempts = 0;
let maxReconnectAttempts = 10;
let baseReconnectDelay = 1000; // Start with 1 second
let maxReconnectDelay = 30000; // Max delay of 30 seconds

// Connection health monitoring
let lastActivityTime = Date.now();
let heartbeatInterval = null;
// 2x the server's heartbeat interval (FETCH_TIMEOUT in sse.py). This only holds
// because the server sends heartbeats as real `data:` events; if it ever goes
// back to SSE comments they will not reach onmessage and every idle connection
// gets torn down on this timer.
let connectionHealthTimeout = 30000;

// JetStream-backed SSE: per-tab session, dedupe by seq, ack after process.
const SESSION_ID_KEY = "sse_session_id";
const LAST_SEQ_KEY = "sse_last_seq";
const ACK_BATCH_MS = 500;
const ACK_BATCH_MAX = 200;

let pendingAcks = [];
let ackFlushTimer = null;

function getSessionId() {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
        id = (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
}

function getLastSeq() {
    const raw = sessionStorage.getItem(LAST_SEQ_KEY);
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function setLastSeq(seq) {
    // Advance only — never roll back. async onmessage handlers can interleave;
    // out-of-order writes here would let an older checkpoint clobber a newer one,
    // which on reconnect would re-deliver msgs that were already processed.
    const cur = getLastSeq();
    if (cur != null && seq <= cur) return;
    sessionStorage.setItem(LAST_SEQ_KEY, String(seq));
}

// Ordering guard, per state slot. Every seq'd event is a full-value write
// addressed by (request_id, key) -> target_component_address[key]; ordering
// only matters between writes to the SAME slot. Redeliveries (nak'd back from
// a superseded connection, ack_wait expiry) legitimately arrive after newer
// seqs for OTHER slots — a global "seq <= lastSeq" drop discards those
// forever even though they were never rendered (e.g. a files-list data
// payload lost behind its own completed flag). Per-slot guarding keeps the
// stale-write protection without the loss. LAST_SEQ_KEY remains a
// checkpoint only (sent as last_seq on connect), not a render filter.
const appliedSeqs = new Map(); // "request_id|key" -> highest seq applied
const APPLIED_SEQS_MAX = 1000;

function slotOf(parsed) {
    return `${parsed.request_id ?? "_global"}|${parsed.key ?? ""}`;
}

function isStaleForSlot(parsed, seq) {
    const last = appliedSeqs.get(slotOf(parsed));
    return last != null && seq <= last;
}

function markApplied(parsed, seq) {
    const slot = slotOf(parsed);
    appliedSeqs.delete(slot); // refresh insertion order so eviction is ~LRU
    appliedSeqs.set(slot, seq);
    if (appliedSeqs.size > APPLIED_SEQS_MAX) {
        for (const k of appliedSeqs.keys()) {
            appliedSeqs.delete(k);
            if (appliedSeqs.size <= APPLIED_SEQS_MAX) break;
        }
    }
}

function queueAck(seq) {
    pendingAcks.push(seq);
    if (ackFlushTimer) return;
    ackFlushTimer = setTimeout(flushAcks, ACK_BATCH_MS);
}

function flushAcks() {
    ackFlushTimer = null;
    if (pendingAcks.length === 0) return;
    const session_id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!session_id) {
        pendingAcks = [];
        return;
    }
    // Dedupe (re-ack on dedupe-drop can pile duplicates into the queue).
    const uniqueSeqs = Array.from(new Set(pendingAcks));
    pendingAcks = [];
    // One request per window instead of one per seq: a streaming response acks
    // per event, so a POST per seq turns into a burst every ACK_BATCH_MS.
    // Chunked to bound payload size on large replay bursts.
    //
    // Routed through apiFetch so a 401 here is treated as the session ending.
    // EventSource cannot report a status code on failure, so this batched ack is
    // the one place the stream can notice its own session has gone.
    for (let i = 0; i < uniqueSeqs.length; i += ACK_BATCH_MAX) {
        const seqs = uniqueSeqs.slice(i, i + ACK_BATCH_MAX);
        apiFetch(`/sse/ack-batch`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id, seqs }),
            keepalive: true,
        }).catch(() => { /* best-effort; redelivery handles failure */ });
    }
}

// Best-effort flush when tab is hidden/unloaded so server's durable cursor
// advances before the connection drops. Without this, recently processed seqs
// would replay on reconnect and (without re-ack) loop until backpressure cap.
function flushAcksOnHide() {
    if (ackFlushTimer) {
        clearTimeout(ackFlushTimer);
        ackFlushTimer = null;
    }
    flushAcks();
}

if (typeof window !== "undefined") {
    window.addEventListener("pagehide", flushAcksOnHide);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushAcksOnHide();
    });
}

export function clearSseSession() {
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(LAST_SEQ_KEY);
    pendingAcks = [];
    if (ackFlushTimer) {
        clearTimeout(ackFlushTimer);
        ackFlushTimer = null;
    }
}

export const connected = writable("false");

// Update activity timestamp (called on any SSE activity)
function updateLastActivity() {
    lastActivityTime = Date.now();
}

// Check if connection is stale (no activity beyond expected keep-alive interval)
function checkConnectionHealth() {
    const timeSinceLastActivity = Date.now() - lastActivityTime;
    
    if (timeSinceLastActivity > connectionHealthTimeout) {
        // console.warn(`SSE connection appears stale. Last activity: ${timeSinceLastActivity}ms ago`);
        
        // Close current connection and trigger reconnection
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
        
        handleSSEDisconnect();
    }
}

// Start connection health monitoring
function startHeartbeatMonitoring() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
    }
    
    // Check connection health every 10 seconds
    heartbeatInterval = setInterval(checkConnectionHealth, 10000);
    updateLastActivity(); // Reset timer when starting monitoring
}

// Stop connection health monitoring
function stopHeartbeatMonitoring() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
    }
}


export function replaceNestedDict(obj, keys, value) {
    if (keys == undefined) {
        console.error("keys is undefined for replaceNestedDict", obj,);
        return;
    }
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
}

export function updateNestedDict(obj, keys, value) {
    if (keys == undefined) {
        console.error("keys is undefined for updateNestedDict", obj,);
        return;
    }
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
            current[keys[i]] = {};
        }
        current = current[keys[i]];
    }
    if (typeof value === 'object' && value !== null) {
        current[keys[keys.length - 1]] = {
            ...current[keys[keys.length - 1]],
            ...value,
        };
    } else {
        current[keys[keys.length - 1]] = value;
    }
}

export function deleteNestedDict(obj, keys) {
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
            return; // Key path doesn't exist, nothing to delete
        }
        current = current[keys[i]];
    }
    delete current[keys[keys.length - 1]];
}
export function getNestedDict(obj, keys) {
    // Base case: no more keys to process
    if (keys.length === 0) {
        return obj;
    }

    // Base case: current object is undefined or doesn't have the key
    if (obj === undefined || obj === null || !Object.prototype.hasOwnProperty.call(obj, keys[0])) {
        return undefined;
    }

    // Recursive case: get first key and process remaining keys
    const [firstKey, ...remainingKeys] = keys;
    return getNestedDict(obj[firstKey], remainingKeys);
}

export function connectSSE() {
    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }

    // Stop any existing heartbeat monitoring
    stopHeartbeatMonitoring();

    const sseUrl = new URL(`${SSE_BASE_URL}/sse`);
    sseUrl.searchParams.set("session_id", getSessionId());
    const lastSeq = getLastSeq();
    if (lastSeq != null) {
        sseUrl.searchParams.set("last_seq", String(lastSeq));
    }

    try {
        eventSource = new EventSource(sseUrl, { withCredentials: true });

        eventSource.onopen = () => {
            // console.log("SSE connected");
            connected.set("true");
            // Reset reconnect attempts on successful connection
            reconnectAttempts = 0;
            // Start monitoring connection health
            startHeartbeatMonitoring();
            updateLastActivity();
        };

        eventSource.onmessage = async (event) => {
            // Update activity timer on any message
            updateLastActivity();

            try {
                const parsed = JSON.parse(event.data);

                if (parsed.type === "log") {
                    if (parsed.sub_type === "heartbeat") {
                        // Server liveness ping. updateLastActivity() above has
                        // already recorded it, which is the entire point: the
                        // server must send this as a real event and never as an
                        // SSE comment (`: keep-alive`), because comment lines
                        // are dropped during dispatch and never reach
                        // onmessage — leaving checkConnectionHealth to tear
                        // down a perfectly healthy connection every 30s.
                        return;
                    }
                    if (parsed.message === "Connected successfully!") {
                        // Server reports the stream's high-water mark on init. A
                        // checkpoint above it means the JetStream stream was
                        // recreated (seqs restarted) — a stale checkpoint would
                        // make the guards drop every new msg. Bypasses
                        // setLastSeq's advance-only rule on purpose.
                        const hw = (typeof parsed.stream_seq === "number") ? parsed.stream_seq : null;
                        const last = getLastSeq();
                        if (hw != null && last != null && last > hw) {
                            console.warn(`SSE stream reset detected (checkpoint ${last} > stream ${hw}) — dropping checkpoint`);
                            sessionStorage.setItem(LAST_SEQ_KEY, String(hw));
                            appliedSeqs.clear();
                        }
                        return;
                    }
                    if (parsed.sub_type === "takeover") {
                        // Another connection took over this session_id (usually a
                        // duplicated tab — "Duplicate Tab" copies sessionStorage).
                        // Regenerate our id before the auto-reconnect so both
                        // connections settle on distinct sessions instead of
                        // kicking each other forever. Keep LAST_SEQ: the fresh
                        // consumer replays from the checkpoint.
                        console.warn("SSE session takeover — regenerating session id");
                        sessionStorage.removeItem(SESSION_ID_KEY);
                        return;
                    }
                    return;
                }

                const seq = (typeof parsed.seq === "number") ? parsed.seq : null;

                if (seq != null) {
                    // Drop only redeliveries that are duplicates or stale for
                    // their OWN slot; still re-ack so the server's durable
                    // cursor advances (else redelivery loops trip backpressure,
                    // stalling all subsequent msgs).
                    if (isStaleForSlot(parsed, seq)) {
                        queueAck(seq);
                        return;
                    }
                    // Gaps mean other users' msgs on the shared stream, not
                    // loss. Big jumps may still be worth surfacing.
                    const last = getLastSeq();
                    if (last != null && seq - last > 1000) {
                        console.warn(`SSE seq jump ${last} -> ${seq} (gap ${seq - last})`);
                    }
                }

                await handleServerEvent(parsed);

                // Delta msgs (and other no-seq payloads) are server-auto-acked: render only.
                if (seq == null) return;

                // Record per-slot watermark + reconnect checkpoint, then ack.
                markApplied(parsed, seq);
                setLastSeq(seq);
                queueAck(seq);
            } catch (error) {
                console.error("Failed to parse SSE message:", error);
                console.error("Raw event data:", event.data);
                toast.push("Failed to parse server message", { classes: ["warn"] });
            }
        };

        eventSource.onerror = (event) => {
            console.error("SSE error:", event);
            
            // Stop heartbeat monitoring on error
            stopHeartbeatMonitoring();
            
            // Close the current connection
            if (eventSource) {
                eventSource.close();
                eventSource = null;
            }
            
            handleSSEDisconnect();
        };
    } catch (error) {
        console.error("Failed to create SSE connection:", error);
        stopHeartbeatMonitoring();
        handleSSEDisconnect();
    }
}

// Enhanced disconnect handler with exponential backoff
const handleSSEDisconnect = async () => {
    connected.set("false");
    // console.log("SSE connection closed");
    
    // Stop heartbeat monitoring
    stopHeartbeatMonitoring();
    
    // A reconnect refused because the login expired fires the same error as a
    // network drop, so retrying used to leave every click on "No connection
    // to the server". Go to the login screen instead.
    if (await endSessionIfExpired()) return;

    if (reconnectAttempts >= maxReconnectAttempts) {
        console.error(`Max reconnection attempts (${maxReconnectAttempts}) reached. Stopping reconnection.`);
        toast.push("Connection lost. Please refresh the page to reconnect.", { classes: ["error"] });
        return;
    }
    
    reconnectAttempts++;
    
    // Exponential backoff: delay = baseDelay * 2^(attempts-1) + jitter
    const exponentialDelay = Math.min(
        baseReconnectDelay * Math.pow(2, reconnectAttempts - 1),
        maxReconnectDelay
    );
    
    // Add random jitter (±25%) to prevent thundering herd
    const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
    const finalDelay = Math.max(exponentialDelay + jitter, 1000);
    
    // console.log(`Reconnecting to updates... (attempt ${reconnectAttempts}/${maxReconnectAttempts}) in ${Math.round(finalDelay)}ms`);
    
    setTimeout(() => {
        // Check if we're still disconnected and user is active before attempting reconnection
        const user = get(current_user);
        if (get(connected) === "false" && user && user.enabled !== false) {
            connectSSE();
        }
    }, finalDelay);
};

// Cleanup function to be called when the app unmounts or user navigates away
export function disconnectSSE() {
    console.log("Disconnecting SSE...");
    reconnectAttempts = 0; // Reset attempts to prevent auto-reconnection

    // Stop heartbeat monitoring
    stopHeartbeatMonitoring();

    // Flush pending acks before tearing down so server's durable cursor advances.
    // Without this, the next connect (e.g. login) re-delivers everything from the
    // last server-acked seq.
    flushAcksOnHide();

    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }

    connected.set("false");
}

// Each streamed token used to update global_state, and every card on the page
// re-renders on each update, so the work grew with the square of an answer's
// length: 25k-character Defend answers froze Safari. Tokens are collected per
// target and applied together a few times a second.
const DELTA_FLUSH_MS = 100;
const pending_deltas = new Map(); // JSON of the target address -> { address, text }
let delta_timer = null;

function queueDelta(address, text) {
    const slot = JSON.stringify(address);
    const pending = pending_deltas.get(slot);
    if (pending) {
        pending.text += text;
    } else {
        pending_deltas.set(slot, { address, text });
    }
    if (!delta_timer) delta_timer = setTimeout(flushDeltas, DELTA_FLUSH_MS);
}

function flushDeltas() {
    clearTimeout(delta_timer);
    delta_timer = null;
    if (pending_deltas.size === 0) return;
    const deltas = [...pending_deltas.values()];
    pending_deltas.clear();
    perfMonitor.measureUpdate(() => {
        global_state.update(s => {
            for (const { address, text } of deltas) {
                updateNestedDict(s, address, (getNestedDict(s, address) ?? "") + text);
            }
            return s;
        });
    });
}

const handleServerEvent = async (parsed) => {
    // Any other event (the final text, a reset, completed, an error) must land
    // after the tokens that came before it.
    if (parsed.sub_type !== "delta") flushDeltas();
    const request_id = parsed.request_id;
    if (!request_id) {
        if (parsed.type === "error") {
            toast.push(parsed.message, { classes: ["warn"] });
        }
        else if (parsed.type === "notification") {
            toast.push(JSON.stringify(parsed.message));
        }
        else if (parsed.type === "info") {
            // console.log("%cinfo%c", "color: #2196F3; font-weight: bold", "color: #4CAF50", parsed.message);
            toast.push(parsed.message);
        }
        return;
    }

    let component_address = [];
    let target_component_address = [];
    try {
        component_address = get(requests_component_lookup)[request_id].request_origin_component_address;
        target_component_address = get(requests_component_lookup)[request_id].target_component_address;
    } catch (e) {
        console.error("request_id not found in requests_component_lookup", request_id);
        return;
    }


    const response_key = parsed.key;

    if (parsed.sub_type !== "delta") {
        console.log(
            "%cRequest ID: %c%s\n%cComponent Address: %c%s\n%cTarget Component Address: %c%s\n%cType: %c%s\n%cSub Type: %c%s\n%cKey: %c%s\n%cMessage: %c%s",
            "color: #1E88E5; font-weight: bold", "color: #43A047", parsed.request_id,
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(component_address),
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(target_component_address[response_key]),
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(parsed.type),
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(parsed.sub_type),
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(parsed.key),
            "color: #1E88E5; font-weight: bold", "color: #43A047", JSON.stringify(parsed.message)
        );
    }
    
    if (parsed.type === "response") {
        if (parsed.sub_type === "update") {
            // A function target receives the payload instead of having it
            // written into global_state. For results too large to persist in
            // localStorage (the source trace's sentence list); a reload drops
            // the function from the persisted lookup and the caller asks again.
            if (typeof target_component_address[response_key] === "function") {
                target_component_address[response_key](parsed.message);
                return;
            }
            perfMonitor.measureUpdate(() => {
                global_state.update(s => {
                    if (target_component_address[response_key]) {
                        updateNestedDict(s, target_component_address[response_key], parsed.message);
                    }
                    return s;
                });
            });

        } else if (parsed.sub_type === "delete") {
            // console.log("delete from server", target_component_address[response_key]);
            perfMonitor.measureUpdate(() => {
                global_state.update(s => {
                    deleteNestedDict(s, target_component_address[response_key]);
                    return s;
                });
            });
        } else if (parsed.sub_type === "delta") {
            if (target_component_address[response_key]) {
                queueDelta(target_component_address[response_key], parsed.message);
            }
        }
    }
    else if (parsed.type === "error") {
        toast.push(parsed.message, { classes: ["warn"] });
        // A failed request never sends its `completed` event, so a download's
        // spinner would spin until its watchdog expires. Release it now.
        // Guarded on base64_file so only download-shaped requests are touched —
        // generation requests use `completed` as a dependency gate and must not
        // be marked done on failure.
        if (target_component_address?.base64_file && target_component_address?.completed) {
            global_state.update(s => {
                updateNestedDict(s, target_component_address.completed, "true");
                return s;
            });
        }
        // Generation requests are the other half of that problem. `completed` is
        // their dependency gate and must stay false on failure, but the spinner
        // reads its own `loading` flag, and nothing turns that off when a request
        // dies — the section spins on long after the error toast has gone. Clear
        // it here: the spinner stops without anything downstream being told the
        // work finished.
        if (target_component_address?.loading) {
            global_state.update(s => {
                updateNestedDict(s, target_component_address.loading, "false");
                return s;
            });
        }
        if (parsed.message.includes("request id")) {
            // console.log("request id  found", parsed);
            try {
                const request_id_match = parsed.message.match(/request id: `([^`]+)`/);
                if (request_id_match) {
                    await navigator.clipboard.writeText(request_id_match[1]);
                } else {
                    await navigator.clipboard.writeText(parsed.message);
                }
                toast.push("Copied to clipboard", { classes: ["success"] });
            } catch (err) {
                console.error('Failed to copy message to clipboard:', err);
                toast.push("Failed to copy message to clipboard", { classes: ["error"] });
            }
        } else {
            // console.log("request id not found", parsed);
        }

    }
    else if (parsed.type === "info") {
        // Cards that run together (Submit all, five at a time) each send the
        // same notice, such as "no project files found". Show it once.
        if (!get(toasts).some((t) => t.message === parsed.message)) {
            toast.push(parsed.message);
        }
    }
    else {
        console.error("unknown event", parsed);
    }

};
